import { action, computed, observable } from 'mobx';
import moment from 'moment';

import { translate } from 'services/i18nService';
import NotificationService from 'services/NotificationService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import { NOTIFICATION_TYPE } from 'enum/messages';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import { NEW_SHOWS_STATE } from 'enum/videoPlayer';
import { isStringHTMLTag } from 'utils/helpers';

import { BOT_TYPES } from './consts';
import chatBotsService from '.';


class ChatBotsStore {
  @observable botsInfo = [];
  @observable botStatus = APIStates.INITIAL;
  @observable saveBotStatus = APIStates.INITIAL;
  @observable broadcasterAutoNotificationBot = {};
  @observable broadcasterWelcomeBot = {};
  @observable broadcasterTipResponseBot = {};
  @observable messageIndex = 0;

  @action handleBotsInfo = (data) => {
    this.botsInfo = data;
    const timeBasedBot = this.botsInfo.filter((bot) => bot.botType === BOT_TYPES.AUTO_NOTIFICATION_BOT);
    const welcomeBot = this.botsInfo.filter((bot) => bot.botType === BOT_TYPES.WELCOME_BOT);
    const tipResponseBot = this.botsInfo.filter((bot) => bot.botType === BOT_TYPES.TIP_RESPONSE_BOT);

    if (timeBasedBot.length > 0 && timeBasedBot[0].details) {
      this.broadcasterAutoNotificationBot = {
        0: timeBasedBot[0].details[0]?.message,
        1: timeBasedBot[0].details[1]?.message,
        2: timeBasedBot[0].details[2]?.message,
        3: timeBasedBot[0].details[3]?.message,
        4: timeBasedBot[0].details[4]?.message,
        5: timeBasedBot[0].details[5]?.message,
      };
    }

    if (welcomeBot.length > 0) {
      this.broadcasterWelcomeBot = {
        0: welcomeBot[0].details[0]?.message,
      };
    }

    if (tipResponseBot.length > 0) {
      this.broadcasterTipResponseBot = {
        details: tipResponseBot[0].details,
      };
    }
  };

  @action setNotificationBotMessages = (name, value) => {
    if (!isStringHTMLTag(value)) {
      const copy = { ...this.broadcasterAutoNotificationBot };

      if (value === '') {
        delete copy[name];
      } else {
        copy[name] = value;
      }

      this.broadcasterAutoNotificationBot = copy;
    }
  };

  @action setWelcomeBotMessages = (name, value) => {
    if (!isStringHTMLTag(value)) {
      const copy = { ...this.broadcasterWelcomeBot };

      if (value === '') {
        delete copy[name];
      } else {
        copy[name] = value;
      }

      this.broadcasterWelcomeBot = copy;
    }
  };

  @computed get isAnyBotActive() {
    for (const bot of this.botsInfo) {
      if (bot.enabled) {
        return true;
      }
    }

    return false;
  }

  @computed get allActiveBots() {
    const result = [];
    for (const bot of this.botsInfo) {
      if (bot.enabled) {
        result.push(bot.botType);
      }
    }

    return result;
  }

  isBroadcasterInPrivateOrGroupShow = () => {
    const { newShowsState } = FirebaseStore;
    const { INSIDE_GS, INSIDE_PS } = NEW_SHOWS_STATE;
    const isInPrivateOrGroupShow = [INSIDE_GS, INSIDE_PS].includes(newShowsState);

    return isInPrivateOrGroupShow;
  };

  isNotification = (value) => {
    if (value === NOTIFICATION_TYPE.MESSAGE) {
      return false;
    }

    const isAnyTypeofNotification = Object.values(NOTIFICATION_TYPE).includes(value);

    return isAnyTypeofNotification;
  };

  get shouldStartPostingMessages() {
    const { messages } = ChatStore;
    const { user: { username } } = UserStore;

    const lastThreeMesagesIndex = -3;
    const lastThreeMesages = messages.slice(lastThreeMesagesIndex);
    const lastThreeMesagesNotifications = lastThreeMesages.some((message) => this.isNotification(message.type));
    const lastThreeMessagesFromBots = lastThreeMesages.some((message) => message.senderOriginalUsername === username);
    const shouldPost =
      !(this.isBroadcasterInPrivateOrGroupShow() ||
        lastThreeMessagesFromBots ||
        lastThreeMesagesNotifications ||
        messages.length === 0
      );

    return shouldPost;
  }

  checkConditionsAndStartAutoMessages = () => {
    const timeBasedBot = this.botsInfo.filter((bot) => bot.botType === BOT_TYPES.AUTO_NOTIFICATION_BOT);

    if (this.shouldStartPostingMessages) {
      const data = this.makeMessageFromData(timeBasedBot[0]);
      this.startAutoMessages(data);
    }
  };

  makeMessageFromData = (data) => {
    const { details, botType, username } = data;
    const messageValues = [];

    for (let i = 0; i < details.length; i += 1) {
      const messageDetails = details[i];
      messageDetails.botType = botType;
      messageDetails.username = username;
      messageDetails.timestamp = Date.now();
      messageValues.push(messageDetails);
    }

    return messageValues;
  };

  startAutoMessages = (messages) => {
    const { sendMainChatMessage } = ChatStore;
    const { user: { username, accessHash } } = UserStore;

    const flatMessages = [].concat(...messages);
    const messageDetails = flatMessages[this.messageIndex % flatMessages.length];

    sendMainChatMessage(username, accessHash, username, messageDetails?.message);
    this.messageIndex = (this.messageIndex + 1) % flatMessages.length;
  };

  sendTippingBotMessage = (message, roomName) => {
    if (!UserStore.hasCF(allUserGroups.CHAT_BOTS)) {
      return;
    }

    const isTipBotEnabled = this.getEnabledStatusByBotType(BOT_TYPES.TIP_RESPONSE_BOT);
    if (!isTipBotEnabled || this.isUserAnonymous(message.senderOriginalUsername)) {
      return;
    }

    const { senderOriginalUsername } = message || {};
    const antiSpamDelayInSeconds = 300;
    this.tippingUsers = this.tippingUsers || {};
    const lastMessageTimestamp = this.tippingUsers[senderOriginalUsername];
    const diff = lastMessageTimestamp ? moment().diff(lastMessageTimestamp, 'seconds') : Infinity;
    const canSendMessage = !senderOriginalUsername || diff > antiSpamDelayInSeconds;

    if (!canSendMessage) {
      return;
    }

    const { details } = this.broadcasterTipResponseBot;
    const parsedNotificationExtra = JSON.parse(message.notificationExtra);
    const tipAmount = parseInt(message.gameName ? parsedNotificationExtra?.p : message.notificationExtra, 10);
    const { user } = UserStore;
    const { sendMainChatMessage } = ChatStore;
    const sortedOptions = details.sort((a, b) => {
      if (a.threshold === b.threshold) {
        return 0;
      }

      if (a.threshold > b.threshold) {
        return -1;
      }

      return 1;
    });

    const currentThreshold = sortedOptions.find(
      (option) => tipAmount >= option.threshold
    );

    if (!currentThreshold) {
      return;
    }

    const botMessage = currentThreshold.message.replaceAll('{user}', message.senderOriginalUsername);
    const messageDelay = 4 * SECOND_AS_MILLISECONDS;
    setTimeout(() => sendMainChatMessage(user.username, user.accessHash, roomName, botMessage), messageDelay);
    this.tippingUsers[senderOriginalUsername] = moment();
  };

  getEnabledStatusByBotType = (botType) => {
    for (const bot of this.botsInfo) {
      if (bot.botType === botType) {
        return bot.enabled;
      }
    }

    return false;
  };

  getIdByBotType = (botType) => {
    for (const bot of this.botsInfo) {
      if (bot.botType === botType) {
        return bot.id;
      }
    }

    return null;
  };

  setUserHasEnteredRoomCookie = (profileName) => {
    const cookieExpires = new Date();
    cookieExpires.setHours(new Date().getHours() + 2);
    localStorage.setItem(`hasBeenIn${profileName}RoomInLastTwoHours`, true, {
      path: '/',
      expires: cookieExpires,
    });
  };

  isUserAnonymous = (username) => {
    const { sortedUsers } = FirebaseStore;

    const currentUsername = username?.toLowerCase();
    const currentUser = sortedUsers.find(
      (item) => item.originalUsername?.toLowerCase() === currentUsername
    );

    return Boolean(currentUser?.stealthLevel);
  };

  checkWelcomeBotStatusAndSendMessage = async() => {
    const { chatSettingToken } = FirebaseStore;
    const { profileInfo } = ProfileStore;
    const { userId } = profileInfo;
    const performerName = profileInfo.username;
    const { user: { username } } = UserStore;

    if (this.isBroadcasterInPrivateOrGroupShow() || this.isUserAnonymous(username)) {
      return;
    }

    const lsFlag = `hasBeenIn${performerName}RoomInLastTwoHours`;
    const hasBeenInRoomInLastTwoHours = Boolean(localStorage.getItem(lsFlag));
    if (hasBeenInRoomInLastTwoHours) {
      return;
    }

    const botData = await chatBotsService.getWelcomeBotInfoForViewer({ token: chatSettingToken, userId });
    const isWelcomeBotEnabled = botData?.enabled;
    if (!isWelcomeBotEnabled) {
      return;
    }

    const messageValue = botData.details[0].message;
    const message = messageValue.replaceAll('{user}', username);

    setTimeout(() => {
      ChatStore.addMessage({
        type: NOTIFICATION_TYPE.MESSAGE,
        message,
        senderOriginalUsername: performerName,
        messageKey: performerName,
      });
    }, 5 * SECOND_AS_MILLISECONDS);

    this.setUserHasEnteredRoomCookie(performerName);
  };

  @computed get botActionPending() {
    return this.botStatus === APIStates.PENDING;
  }

  @computed get botSavePending() {
    return this.saveBotStatus === APIStates.PENDING;
  }

  @action updateBotDetails = async({ actionType, botDetails, callback }) => {
    const { chatSettingToken } = FirebaseStore;

    const chatBotId = this.getIdByBotType(botDetails.botType);
    const isEnabled = this.getEnabledStatusByBotType(botDetails.botType);

    let newEnabledState = undefined;
    if (actionType === 'SAVE') {
      this.saveBotStatus = APIStates.PENDING;
      newEnabledState = isEnabled;
    } else {
      this.botStatus = APIStates.PENDING;
      newEnabledState = !isEnabled;
    }

    const apiCall = !chatBotId ? chatBotsService.createBot : chatBotsService.updateBot;
    const params = {
      ...botDetails,
      chatBotId,
      enabled: newEnabledState,
      token: chatSettingToken,
    };

    const { error: apiCallError } = await apiCall(params);

    if (!apiCallError) {
      callback?.();

      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.SUCCESS,
        text: translate('v2broadcast.changes.saved'),
      });
    } else {
      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.ERROR,
        text: translate('account.errorGeneric'),
      });
    }

    if (actionType === 'SAVE') {
      this.saveBotStatus = APIStates.INITIAL;
    } else {
      this.botStatus = APIStates.INITIAL;
    }
  };
}

export default new ChatBotsStore();
